import dayjs from 'dayjs'
import { Note, isCalendarNote, filenameToDate } from '../../../utils/note'

// Helper function to get source note title for task display
export function getSourceNoteTitle(note: Note): string {
  if (isCalendarNote(note.noteType)) {
    // For calendar notes, show a shorter format
    const { date, timeframe } = filenameToDate(note.filename)
    if (date && timeframe) {
      return formatCalendarNoteTitleShort(date, timeframe)
    }
    return note.filename
  }

  // For project notes, use the title or filename
  return note.title ?? note.filename
}

// Helper function to format calendar note titles in short format for source note display
export function formatCalendarNoteTitleShort(
  date: dayjs.Dayjs,
  timeframe: string
): string {
  switch (timeframe) {
    case 'week': {
      const weekNumber = date.week()
      const year = date.year()
      const isCurrentYear = year === new Date().getFullYear()
      return isCurrentYear
        ? `Week ${weekNumber.toString()}`
        : `Week ${weekNumber.toString()}, ${year.toString()}`
    }
    case 'month': {
      const isCurrentYear = date.year() === new Date().getFullYear()
      return date.format(isCurrentYear ? 'MMM' : 'MMM YYYY')
    }
    case 'quarter': {
      const quarter = date.quarter()
      const year = date.year()
      const isCurrentYear = year === new Date().getFullYear()
      return isCurrentYear
        ? `Q${quarter.toString()}`
        : `Q${quarter.toString()}, ${year.toString()}`
    }
    case 'year': {
      return date.format('YYYY')
    }
    default: {
      // Daily notes - use short format
      const isCurrentYear = date.year() === new Date().getFullYear()
      const today = dayjs()

      if (date.isSame(today, 'day')) {
        return 'Today'
      } else if (date.isSame(today.add(1, 'day'), 'day')) {
        return 'Tomorrow'
      } else if (date.isSame(today.subtract(1, 'day'), 'day')) {
        return 'Yesterday'
      } else {
        return date.format(isCurrentYear ? 'D MMM' : 'D MMM YYYY')
      }
    }
  }
}

// Helper function to format scheduled date
export function formatScheduledDate(dateString: string): string {
  if (dateString === 'today') {
    return 'Today'
  }

  if (dateString === 'tomorrow') {
    return 'Tomorrow'
  }

  try {
    // Parse the date string as a local date to avoid timezone issues
    // Split the date string and create a Date object with local timezone
    const parts = dateString.split('-')
    if (parts.length === 3) {
      const year = Number.parseInt(parts[0], 10)
      const month = Number.parseInt(parts[1], 10) - 1 // Month is 0-indexed
      const day = Number.parseInt(parts[2], 10)
      const date = new Date(year, month, day)

      return date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    }

    // Fallback to original method if parsing fails
    const date = new Date(dateString)
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  } catch {
    return dateString
  }
}

// Helper function to get note title (extracted from filter component)
export function getNoteTitle(note: Note): string {
  if (isCalendarNote(note.noteType)) {
    // For calendar notes, use the same logic as NoteTitle component
    const { date, timeframe } = filenameToDate(note.filename)
    if (date && timeframe) {
      return formatCalendarNoteTitle(date, timeframe)
    }
    return note.filename
  }

  // For project notes, use the title or filename
  return note.title ?? note.filename
}

// Helper function to format calendar note titles based on timeframe
export function formatCalendarNoteTitle(
  date: dayjs.Dayjs,
  timeframe: string
): string {
  switch (timeframe) {
    case 'week': {
      const weekNumber = date.week()
      const year = date.year()
      const isCurrentYear = year === new Date().getFullYear()
      const startOfWeek = date.startOf('week')
      const endOfWeek = date.endOf('week')
      const dateFormat = isCurrentYear ? 'MMM D' : 'MMM D, YYYY'
      const title = isCurrentYear
        ? `Week ${weekNumber.toString()}`
        : `Week ${weekNumber.toString()}, ${year.toString()}`
      return `${title} (${startOfWeek.format(dateFormat)} - ${endOfWeek.format(dateFormat)})`
    }
    case 'month': {
      const isCurrentYear = date.year() === new Date().getFullYear()
      return date.format(isCurrentYear ? 'MMMM' : 'MMMM YYYY')
    }
    case 'quarter': {
      const quarter = date.quarter()
      const year = date.year()
      const isCurrentYear = year === new Date().getFullYear()
      const startOfQuarter = date.startOf('quarter')
      const monthFormat = isCurrentYear ? 'MMM' : 'MMM YYYY'
      const months = [0, 1, 2]
        .map((m) => startOfQuarter.add(m, 'month').format(monthFormat))
        .join(' - ')
      const title = isCurrentYear
        ? `Q${quarter.toString()}`
        : `Q${quarter.toString()}, ${year.toString()}`
      return `${title} (${months})`
    }
    case 'year': {
      return date.format('YYYY')
    }
    default: {
      // Daily notes
      const isCurrentYear = date.year() === new Date().getFullYear()
      const format = isCurrentYear ? 'ddd, D MMM' : 'ddd, D MMM YYYY'
      return date.calendar(undefined, {
        lastDay: `[Yesterday], ${format}`,
        sameDay: `[Today], ${format}`,
        nextDay: `[Tomorrow], ${format}`,
        nextWeek: format,
        lastWeek: format,
        sameElse: format,
      })
    }
  }
}
